import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import PageHeader from "@/components/PageHeader";
import ProvenanceBadge from "@/components/ProvenanceBadge";
import StrategyEvolutionCard from "@/components/StrategyEvolutionCard";
import QuizBanner from "@/components/QuizBanner";
import OnboardingCard from "@/components/OnboardingCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowUpRight, Plus, Sparkles, Brain, RefreshCw, Lock, Loader2, Check, NotebookPen, Pin, Pencil, Trash2, X, Shield } from "lucide-react";
import { toast } from "sonner";
import { coachHints } from "@/lib/mockData";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";

const API = process.env.REACT_APP_BACKEND_URL;

const toneStyles = {
  success: "text-[hsl(122_39%_60%)]",
  info: "text-[hsl(210_25%_68%)]",
  muted: "text-muted-foreground",
};

function fmtRelativeCron(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const diffMin = Math.round((d - now) / 60000);
  if (diffMin < 60) return `in ${Math.max(1, diffMin)} Min.`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `in ${diffH} Std.`;
  return d.toLocaleString("de-DE", { weekday: "short", hour: "2-digit", minute: "2-digit" });
}

function buildKpiTiles(data) {
  if (!data) {
    return [
      { id: "trades-week",   label: "Trades diese Woche",     value: "—", delta: "", hint: "Anzahl dokumentierter Einträge seit Montag", tone: "muted", testid: "kpi-trades-week" },
      { id: "reflected",     label: "Reflektierte Trades",    value: "—", delta: "", hint: "Trades mit ausgefülltem Reflexions-Feld",     tone: "muted", testid: "kpi-reflected" },
      { id: "strategies",    label: "Aktive Strategien",      value: "—", delta: "", hint: "Freigegebene, nicht archivierte Strategien", tone: "muted", testid: "kpi-strategies" },
      { id: "next-sync",     label: "Nächster Broker-Sync",   value: "—", delta: "", hint: "Automatischer Fills-Abruf aller Broker",     tone: "muted", testid: "kpi-next-sync" },
    ];
  }
  const tradesW = data.trades_this_week;
  const refl = data.reflected_this_week;
  const rate = tradesW > 0 ? Math.round((refl / tradesW) * 100) : null;
  return [
    { id: "trades-week",
      label: "Trades diese Woche",
      value: String(tradesW),
      delta: tradesW === 0 ? "noch keine" : (tradesW === 1 ? "1 Eintrag" : `${tradesW} Einträge`),
      hint: "Anzahl dokumentierter Einträge seit Montag",
      tone: tradesW > 0 ? "info" : "muted",
      testid: "kpi-trades-week" },
    { id: "reflected",
      label: "Reflektierte Trades",
      value: String(refl),
      delta: rate === null ? "diese Woche" : `${rate} % dieser Woche`,
      hint: "Trades mit ausgefülltem Reflexions-Feld",
      tone: refl > 0 ? "info" : "muted",
      testid: "kpi-reflected" },
    { id: "strategies",
      label: "Aktive Strategien",
      value: String(data.active_strategies),
      delta: data.active_strategies === 0 ? "keine aktiv" : "in Anwendung",
      hint: "Freigegebene, nicht archivierte Strategien",
      tone: data.active_strategies > 0 ? "info" : "muted",
      testid: "kpi-strategies" },
    { id: "next-sync",
      label: "Nächster Broker-Sync",
      value: fmtRelativeCron(data.next_cron_sync_at),
      delta: "täglich 07:00",
      hint: "Automatischer Fills-Abruf aller Broker",
      tone: "info",
      testid: "kpi-next-sync" },
  ];
}

function usePulse() {
  return useQuery({
    queryKey: ["dashboard-pulse"],
    queryFn: async () => (await axios.get(`${API}/api/dashboard/pulse`, { withCredentials: true })).data,
    staleTime: 30_000,
  });
}

export default function Overview() {
  const pulseQ = usePulse();
  const developmentSeries = pulseQ.data?.weeks || [];
  const activities = pulseQ.data?.activities || [];
  const focus = pulseQ.data?.focus || [];
  const totalTrades = pulseQ.data?.total_trades || 0;
  return <OverviewInner developmentSeries={developmentSeries} activities={activities} focus={focus} totalTrades={totalTrades} />;
}

function OverviewInner({ developmentSeries, activities, focus, totalTrades }) {
  return <OverviewBody developmentSeries={developmentSeries} activities={activities} focus={focus} totalTrades={totalTrades} />;
}

function DashboardNotes() {
  // Freies Notizfeld, debounce-Autosave (~900 ms) gegen /api/auth/me/prefs.
  // Speichert und lädt aus `user.prefs.dashboard_note`. Zeigt kleinen
  // Status-Hinweis rechts unten („Gespeichert" / „Speichere …").
  const { user, updatePrefs } = useAuth();
  const initial = (user && user !== false) ? (user.prefs?.dashboard_note || "") : "";
  const [value, setValue] = useState(initial);
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const timerRef = useRef(null);
  const lastSavedRef = useRef(initial);
  const hydratedRef = useRef(false);

  // Wenn /me nach Login später kommt, initialen Wert einmalig übernehmen.
  useEffect(() => {
    if (hydratedRef.current) return;
    if (user && user !== false) {
      const remote = user.prefs?.dashboard_note || "";
      setValue(remote);
      lastSavedRef.current = remote;
      hydratedRef.current = true;
    }
  }, [user]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const scheduleSave = (next) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (next === lastSavedRef.current) { setStatus("idle"); return; }
    setStatus("saving");
    timerRef.current = setTimeout(async () => {
      try {
        await updatePrefs({ dashboard_note: next });
        lastSavedRef.current = next;
        setStatus("saved");
        setTimeout(() => setStatus((s) => (s === "saved" ? "idle" : s)), 1800);
      } catch (e) {
        setStatus("error");
      }
    }, 900);
  };

  const onChange = (e) => {
    const v = e.target.value.slice(0, 4000);
    setValue(v);
    scheduleSave(v);
  };

  return (
    <div className="mt-6 border-t border-border pt-5" data-testid="dashboard-notes-block">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <NotebookPen className="h-4 w-4 text-primary" strokeWidth={1.5} />
          <div className="overline">Persönliche Notizen</div>
        </div>
        <div className="text-[11px] text-muted-foreground flex items-center gap-2 min-h-[16px]" data-testid="dashboard-notes-status">
          {status === "saving" && (<><Loader2 className="h-3 w-3 animate-spin" /> Speichere …</>)}
          {status === "saved" && (<><Check className="h-3 w-3 text-[hsl(122_39%_60%)]" /> Gespeichert</>)}
          {status === "error" && (<span className="text-[hsl(0_66%_72%)]">Speichern fehlgeschlagen</span>)}
          {status === "idle" && value && <span>{value.length}/4000</span>}
        </div>
      </div>
      <Textarea
        value={value}
        onChange={onChange}
        placeholder="Deine Trading-Routine, offene Fragen, Setups im Kopf, Erinnerungen für die nächste Session …"
        className="min-h-[140px] resize-y bg-secondary/20 border-border/70 focus-visible:ring-primary/40 text-sm leading-relaxed"
        data-testid="dashboard-notes-textarea"
        maxLength={4000}
      />
    </div>
  );
}

function TradingRulesCard() {
  // Trading-Regeln (ex "Hinweise des KI-Coachs"). Werden automatisch aus den
  // Coach-Chats extrahiert und können manuell hinzugefügt/bearbeitet/gelöscht
  // werden. Datenquelle: /api/coach-rules.
  const qc = useQueryClient();
  const rulesQ = useQuery({
    queryKey: ["coach-rules"],
    queryFn: async () => (await axios.get(`${API}/api/coach-rules`, { withCredentials: true })).data,
    staleTime: 15_000,
  });
  const rules = rulesQ.data || [];

  const [adding, setAdding] = useState(false);
  const [newText, setNewText] = useState("");
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");

  const invalidate = () => qc.invalidateQueries({ queryKey: ["coach-rules"] });

  const createMut = useMutation({
    mutationFn: async (text) =>
      (await axios.post(`${API}/api/coach-rules`, { text }, { withCredentials: true })).data,
    onSuccess: () => { setNewText(""); setAdding(false); invalidate(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Regel konnte nicht gespeichert werden."),
  });
  const patchMut = useMutation({
    mutationFn: async ({ id, body }) =>
      (await axios.patch(`${API}/api/coach-rules/${id}`, body, { withCredentials: true })).data,
    onSuccess: () => { setEditId(null); setEditText(""); invalidate(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Regel konnte nicht gespeichert werden."),
  });
  const deleteMut = useMutation({
    mutationFn: async (id) =>
      (await axios.delete(`${API}/api/coach-rules/${id}`, { withCredentials: true })).data,
    onSuccess: invalidate,
  });

  const startEdit = (r) => { setEditId(r.id); setEditText(r.text); };
  const cancelEdit = () => { setEditId(null); setEditText(""); };

  return (
    <div className="card-surface p-6 lg:col-span-4" data-testid="trading-rules-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-primary" strokeWidth={1.5} />
          <div className="overline">Deine Trading-Regeln</div>
        </div>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
            data-testid="rule-add-btn"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={1.5} /> Hinzufügen
          </button>
        )}
      </div>

      {adding && (
        <div className="mb-3 flex gap-2" data-testid="rule-add-form">
          <input
            type="text"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newText.trim()) createMut.mutate(newText.trim());
              if (e.key === "Escape") { setNewText(""); setAdding(false); }
            }}
            placeholder="z. B. Bei Unsicherheit nicht traden."
            autoFocus
            maxLength={180}
            className="flex-1 h-9 rounded-lg bg-background border border-border px-3 text-sm focus:outline-none focus:border-primary"
            data-testid="rule-add-input"
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => newText.trim() && createMut.mutate(newText.trim())}
            disabled={createMut.isPending || !newText.trim()}
            data-testid="rule-add-save"
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setNewText(""); setAdding(false); }}
            data-testid="rule-add-cancel"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {rules.length === 0 ? (
        <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
          Noch keine Regeln festgehalten. Sprich im KI-Coach über ein Prinzip oder
          eine Verhaltensregel — sie wird automatisch extrahiert und erscheint hier.
          Oder leg direkt oben eine eigene Regel an.
        </p>
      ) : (
        <ul className="space-y-2 mt-2" data-testid="trading-rules-list">
          {rules.map((r) => (
            <li
              key={r.id}
              className="group border-l-2 pl-3 py-1.5 border-primary/50 flex items-start justify-between gap-2"
              data-testid={`rule-${r.id}`}
            >
              {editId === r.id ? (
                <>
                  <input
                    type="text"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && editText.trim()) patchMut.mutate({ id: r.id, body: { text: editText.trim() } });
                      if (e.key === "Escape") cancelEdit();
                    }}
                    autoFocus
                    maxLength={180}
                    className="flex-1 h-8 rounded-md bg-background border border-border px-2 text-sm focus:outline-none focus:border-primary"
                    data-testid={`rule-edit-input-${r.id}`}
                  />
                  <button
                    type="button"
                    onClick={() => editText.trim() && patchMut.mutate({ id: r.id, body: { text: editText.trim() } })}
                    className="text-muted-foreground hover:text-foreground"
                    data-testid={`rule-edit-save-${r.id}`}
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="text-muted-foreground hover:text-foreground"
                    data-testid={`rule-edit-cancel-${r.id}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-foreground leading-relaxed">{r.text}</div>
                    <div className="text-[10px] text-muted-foreground mt-1">
                      {r.source === "coach" ? "aus KI-Coach-Chat" : "manuell"}
                    </div>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => startEdit(r)}
                      className="text-muted-foreground hover:text-foreground p-1"
                      aria-label="Regel bearbeiten"
                      data-testid={`rule-edit-${r.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteMut.mutate(r.id)}
                      className="text-muted-foreground hover:text-[hsl(0_66%_72%)] p-1"
                      aria-label="Regel löschen"
                      data-testid={`rule-delete-${r.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      <Link to="/app/ki-coach">
        <Button variant="ghost" className="mt-4 h-9 px-2 text-sm" data-testid="open-coach-link">
          Zum KI-Coach <ArrowUpRight className="ml-1 h-4 w-4" strokeWidth={1.5} />
        </Button>
      </Link>
    </div>
  );
}

function PersonalFocusStats({ series, totalTrades: totalAll }) {
  // Aggregate letzte 6 Wochen aus dem Pulse-Response — zeigt Nutzer:innen
  // die Prozess-Trends auch dann, wenn sie noch nicht viel mit dem Coach
  // gesprochen haben.
  const total6w = (series || []).reduce((a, w) => a + (w.trades || 0), 0);
  if (total6w === 0) {
    if ((totalAll || 0) > 0) {
      return (
        <p className="mt-3 text-base leading-relaxed text-muted-foreground italic" data-testid="personal-focus-window-hint">
          Du hast {totalAll} Trade{totalAll === 1 ? "" : "s"} dokumentiert — sie liegen aber außerhalb der letzten 6 Wochen. Die Gesamtübersicht findest du in der{" "}
          <Link to="/app/analyse" className="text-primary underline underline-offset-2">Analyse</Link>.
        </p>
      );
    }
    return (
      <p className="mt-3 text-base leading-relaxed text-muted-foreground italic">
        Sobald du deine ersten Trades erfasst hast, sammeln wir hier deine Prozess-Zahlen aus den letzten Wochen.
      </p>
    );
  }
  const weeksWithTrades = (series || []).filter((w) => (w.trades || 0) > 0);
  const avg = (key) =>
    weeksWithTrades.length
      ? Math.round(weeksWithTrades.reduce((a, w) => a + (w[key] || 0), 0) / weeksWithTrades.length)
      : 0;
  const stats = [
    { label: "Trades gesamt", value: total6w, hint: "letzte 6 Wochen" },
    { label: "Ø Regeltreue", value: `${avg("regel")}\u00A0%`, hint: "mit Strategie erfasst" },
    { label: "Ø Reflexionsquote", value: `${avg("reflexion")}\u00A0%`, hint: "Reflexion befüllt" },
    { label: "Ø Konsistenz", value: `${avg("konsistenz")}\u00A0%`, hint: "Tage pro Handelswoche" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4" data-testid="personal-focus-stats">
      {stats.map((s) => (
        <div key={s.label} className="rounded-lg border border-border bg-secondary/20 p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{s.label}</div>
          <div className="font-mono text-2xl mt-1">{s.value}</div>
          <div className="text-[11px] text-muted-foreground mt-1">{s.hint}</div>
        </div>
      ))}
    </div>
  );
}

function OverviewBody({ developmentSeries, activities, focus, totalTrades }) {
  const { user, isAdmin } = useAuth();
  const [skillOpen, setSkillOpen] = useState(false);
  const firstName = (user && user !== false)
    ? (user.name || user.email?.split("@")[0] || "").split(/[.\s@]/)[0]
    : "";
  const greeting = firstName ? `Willkommen zurück, ${firstName.charAt(0).toUpperCase() + firstName.slice(1)}.` : "Willkommen zurück.";

  const { data: kpiData } = useQuery({
    queryKey: ["dashboard-kpis"],
    queryFn: async () => {
      const r = await axios.get(`${API}/api/dashboard/kpis`, { withCredentials: true });
      return r.data;
    },
    staleTime: 60_000,
  });
  const kpis = buildKpiTiles(kpiData);

  const skillQuery = useQuery({
    queryKey: ["skill-score"],
    queryFn: async () => {
      const r = await axios.get(`${API}/api/dashboard/skill-score`, { withCredentials: true });
      return r.data;
    },
    enabled: !!isAdmin,
    staleTime: 6 * 60 * 60_000,
    retry: false,
  });
  const skillRefresh = useMutation({
    mutationFn: async () => {
      const r = await axios.post(`${API}/api/dashboard/skill-score/refresh`, {}, { withCredentials: true });
      return r.data;
    },
    onSuccess: (data) => {
      skillQuery.refetch();
      toast.success("Skill-Score aktualisiert", { description: `Neuer Wert: ${data.score}/10` });
    },
    onError: (e) => {
      toast.error("Berechnung fehlgeschlagen", { description: e?.response?.data?.detail || "Bitte in einem Moment erneut versuchen." });
    },
  });
  return (
    <div className="stagger">
      <PageHeader
        eyebrow="Übersicht"
        title={greeting}
        description="Ein Blick auf Entwicklung, Disziplin und Dokumentation der letzten Wochen."
        testid="overview-header"
        actions={
          <Link to="/app/journal">
            <Button data-testid="overview-new-trade-btn" className="rounded-full h-11 px-5">
              <Plus className="mr-2 h-4 w-4" strokeWidth={1.5} /> Neuen Trade erfassen
            </Button>
          </Link>
        }
      />

      <QuizBanner />
      <OnboardingCard />

      <section
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
        data-testid="kpi-grid"
      >
        {kpis.map((k) => (
          <div
            key={k.id}
            data-testid={k.testid}
            className="card-surface p-6 hover:-translate-y-[1px] transition-transform duration-200"
          >
            <div className="overline">{k.label}</div>
            <div className="mt-3 flex items-baseline gap-2">
              <div className="font-mono text-3xl tracking-tight text-foreground">{k.value}</div>
              <div className={`text-xs ${toneStyles[k.tone]}`}>{k.delta}</div>
            </div>
            <div className="mt-3 text-sm text-muted-foreground leading-snug">{k.hint}</div>
          </div>
        ))}
      </section>

      {isAdmin && (
        <section className="mb-8" data-testid="skill-score-section">
          <button
            type="button"
            onClick={() => setSkillOpen(true)}
            className="card-surface p-6 w-full text-left group hover:-translate-y-[1px] transition-transform duration-200 border-primary/20 hover:border-primary/40"
            data-testid="skill-score-tile"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Brain className="h-4 w-4 text-primary" strokeWidth={1.5} />
                  <div className="overline">Trading-Skill-Score</div>
                  <Badge variant="outline" className="rounded-full text-[10px] h-5 px-2 border-primary/30">
                    <Lock className="h-2.5 w-2.5 mr-1" strokeWidth={2} /> Admin · privat
                  </Badge>
                </div>
                <div className="flex items-baseline gap-3 mt-2">
                  {skillQuery.isLoading ? (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin" /> <span className="text-sm">Coach analysiert …</span>
                    </div>
                  ) : skillQuery.error ? (
                    <div className="text-sm text-[hsl(0_66%_72%)]">Konnte nicht berechnet werden — klicke für Details</div>
                  ) : (
                    <>
                      <div className="font-mono text-5xl tracking-tight text-foreground" data-testid="skill-score-value">
                        {skillQuery.data?.score ?? "—"}
                      </div>
                      <div className="text-muted-foreground text-lg font-mono">/ 10</div>
                    </>
                  )}
                </div>
                <div className="mt-2 text-sm text-muted-foreground leading-snug">
                  KI-Coach-Einschätzung deiner Trading-Erfahrung anhand deiner Strategien &amp; Trades.
                  {skillQuery.data?.computed_at && (
                    <span className="ml-1 text-[11px]">
                      Zuletzt aktualisiert: {new Date(skillQuery.data.computed_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                </div>
              </div>
              <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0 mt-1" strokeWidth={1.5} />
            </div>
          </button>

          <Dialog open={skillOpen} onOpenChange={setSkillOpen}>
            <DialogContent className="max-w-2xl" data-testid="skill-score-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Brain className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  Trading-Skill-Score: {skillQuery.data?.score ?? "—"} / 10
                </DialogTitle>
                <DialogDescription>
                  Einschätzung des KI-Coachs auf Basis deiner Strategien, Reflexionen und Trades. Dieser Score ist nur für dich als Admin sichtbar.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {skillQuery.error ? (
                  <div className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
                    {skillQuery.error?.response?.data?.detail || "Der Coach konnte keine Einschätzung liefern. Bitte versuche es erneut."}
                  </div>
                ) : skillQuery.data ? (
                  <>
                    <div className="rounded-lg border border-border bg-secondary/40 p-4">
                      <div className="overline mb-2">Begründung</div>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="skill-score-reasoning">
                        {skillQuery.data.reasoning}
                      </p>
                    </div>
                    {skillQuery.data.data_snapshot && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        {Object.entries(skillQuery.data.data_snapshot).map(([k, v]) => (
                          <div key={k} className="rounded-md border border-border p-3">
                            <div className="text-muted-foreground text-[10px] uppercase tracking-wider">{k.replace(/_/g, " ")}</div>
                            <div className="font-mono text-lg mt-1">{v}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-muted-foreground">Wird berechnet …</div>
                )}
                <div className="flex justify-between items-center pt-2">
                  <div className="text-[11px] text-muted-foreground">
                    {skillQuery.data?.cached ? "Zwischengespeichertes Ergebnis" : "Frisch berechnet"}
                  </div>
                  <Button
                    onClick={() => skillRefresh.mutate()}
                    disabled={skillRefresh.isPending}
                    variant="outline"
                    className="rounded-full h-9"
                    data-testid="skill-score-refresh"
                  >
                    {skillRefresh.isPending ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Berechnet …</>
                    ) : (
                      <><RefreshCw className="h-4 w-4 mr-2" strokeWidth={1.5} /> Neu berechnen</>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </section>
      )}

      <section className="mb-6">
        <StrategyEvolutionCard />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="card-surface p-6 lg:col-span-8" data-testid="development-chart">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="overline">Entwicklung</div>
              <h2 className="font-heading text-xl mt-1">Disziplin & Dokumentation</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Regelkonformität, Reflexionsquote und Konsistenz — sobald du Trades erfasst hast.
              </p>
            </div>
            <Badge variant="secondary" className="rounded-full">6 Wochen</Badge>
          </div>
          {developmentSeries.length === 0 || developmentSeries.every((w) => (w.trades || 0) === 0) ? (
            <div className="h-40 grid place-items-center rounded-lg border border-dashed border-border">
              <div className="text-center px-6">
                <div className="text-sm text-muted-foreground">Noch keine Daten für die Entwicklungskurve.</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Erfasse deinen ersten Trade — nach dem ersten Eintrag entsteht hier deine Kurve.
                </p>
              </div>
            </div>
          ) : (
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={developmentSeries} margin={{ left: -18, right: 4, top: 8, bottom: 0 }}>
                  <CartesianGrid stroke="hsl(217 25% 22%)" vertical={false} />
                  <XAxis dataKey="w" stroke="hsl(220 9% 62%)" tickLine={false} axisLine={false} fontSize={12} />
                  <YAxis stroke="hsl(220 9% 62%)" tickLine={false} axisLine={false} fontSize={12} domain={[0, 100]} unit="%" />
                  <Tooltip
                    contentStyle={{
                      border: "1px solid hsl(217 25% 22%)",
                      borderRadius: 12,
                      background: "hsl(217 33% 17%)",
                      color: "hsl(220 13% 91%)",
                      fontFamily: "Inter",
                      fontSize: 12,
                    }}
                    formatter={(v, n) => [`${v}\u00A0%`, n]}
                  />
                  <Legend
                    verticalAlign="top"
                    height={28}
                    iconType="plainline"
                    wrapperStyle={{ fontSize: 12, color: "hsl(220 9% 62%)" }}
                  />
                  <Line type="monotone" dataKey="regel" name="Regelkonformität" stroke="hsl(192 55% 55%)" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="reflexion" name="Reflexionsquote" stroke="hsl(210 25% 68%)" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="konsistenz" name="Konsistenz" stroke="hsl(32 45% 62%)" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          <DashboardNotes />
        </div>

        <div className="card-surface p-6 lg:col-span-4" data-testid="ai-coach-hints">
          <TradingRulesCard />
        </div>

        <div className="card-surface p-6 lg:col-span-8" data-testid="personal-focus">
          <div className="overline">Persönlicher Fokus</div>
          <h2 className="font-heading text-xl mt-1">Deine Prozess-Zahlen dieser Wochen</h2>
          <PersonalFocusStats series={developmentSeries} totalTrades={totalTrades} />
        </div>

        <div className="card-surface p-6 lg:col-span-4" data-testid="recent-activities">
          <div className="overline mb-3">Letzte Aktivitäten</div>
          {activities.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              Noch keine Aktivitäten. Der Feed füllt sich mit deinem ersten Trade oder Journal-Eintrag.
            </p>
          ) : (
            <ul className="space-y-4">
              {activities.map((a) => (
                <li key={a.id} className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm text-foreground">{a.text}</div>
                    <div className="text-xs text-muted-foreground mt-1">{a.when} · {a.type}</div>
                  </div>
                  <ProvenanceBadge source={a.source} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
